import type { ReactElement, ReactNode } from 'react'
import { Crown } from 'iconoir-react'
import { Link, useLocation } from 'react-router-dom'
import { AGENTS_HUB_PAGES } from 'lib/sidebarItems'
import { PageScroll } from 'app/components/layout/PageScroll'
import { Button } from 'app/components/ui/button'
import { Tabs, TabsList, TabsTrigger } from 'app/components/ui/tabs'
import { TooltipProvider } from 'app/components/ui/tooltip'
import { cn } from 'app/lib/utils'
import { useUser } from 'app/utils'
import { useAgentAccess } from './useAgentAccess'

function SuperCrown(): ReactElement {
  return (
    <span aria-label="Super users only" className="flex size-5 items-center justify-center rounded bg-warning-minimal">
      <Crown width={12} height={12} className="text-warning-strong" />
    </span>
  )
}

/** The switch under an agent's page: Dashboard, Call Log and so on, with a crown on super-user tabs. */
export function AgentSubTabs({
  value,
  onChange,
  tabs,
  superUserTabs,
  trailing,
}: {
  value: string
  onChange: (tab: string) => void
  tabs: string[]
  superUserTabs: string[]
  /** Shown at the row's right end, such as the agent's live status. */
  trailing?: ReactNode
}): ReactElement {
  return (
    <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
      <Tabs value={value} onValueChange={onChange}>
        <TabsList>
          {tabs.map((tab) => (
            <TabsTrigger key={tab} value={tab}>
              <span className="flex items-center gap-1.5">
                {tab}
                {superUserTabs.includes(tab) && <SuperCrown />}
              </span>
            </TabsTrigger>
          ))}
        </TabsList>
      </Tabs>
      {trailing}
    </div>
  )
}

export function AgentsShell({
  children,
  header,
  actions,
}: {
  children: ReactNode
  /** Replaces the title row, for a page with its own filters. */
  header?: ReactNode
  /** Replaces the spike location picker beside the title. */
  actions?: ReactNode
}): ReactElement {
  const { pathname } = useLocation()
  const isSuper = useUser()?.type === `super`
  const access = useAgentAccess()
  const hasProduct = (product?: string | string[]) =>
    !product ||
    [product].flat().some((name) => (name === `AI Agent` ? access.voice : name === `SMS Agent` && access.sms))
  return (
    <TooltipProvider>
      <div className="flex h-full min-h-0 flex-col">
        <div className="min-h-0 flex-1">
          <PageScroll
            contentClassName="scrollbar-gutter-stable pb-14 pt-8"
            header={
              <div className="scrollbar-gutter-stable flex w-full flex-col overflow-hidden">
                {header ?? (
                  <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-3 pb-8">
                    <h1 className="gilroy text-heading-xl text-strong">Agents</h1>
                    {actions}
                  </div>
                )}
                <nav className="no-scrollbar mt-3 flex gap-6 overflow-x-auto whitespace-nowrap border-b border-default">
                  {AGENTS_HUB_PAGES.filter((tab) => (isSuper || !tab.superOnly) && hasProduct(tab.product)).map(
                    (tab) => (
                      <Link
                        key={tab.href}
                        to={tab.href}
                        aria-current={tab.match.test(pathname) ? `page` : undefined}
                        className={cn(
                          `-mb-px flex items-center gap-1.5 border-b-2 pb-4 pt-0.5 text-label-sm`,
                          tab.match.test(pathname)
                            ? `border-(--fg-strong) text-strong`
                            : `border-transparent text-subtle hover:text-strong`,
                        )}
                      >
                        {tab.name}
                        {tab.superOnly && <SuperCrown />}
                      </Link>
                    ),
                  )}
                </nav>
              </div>
            }
          >
            <div className="w-full">{children}</div>
          </PageScroll>
        </div>
      </div>
    </TooltipProvider>
  )
}
